import type {
  IEmbedSessionCountriesQuery,
  IEmbedSessionListQuery,
  IEmbedSessionLiveQuery,
  IEmbedSessionSummaryQuery,
  KnowledgeBaseOwnerType,
} from "@speakai/shared";

export const mediaKeys = {
  all: ["media"] as const,
  lists: () => [...mediaKeys.all, "list"] as const,
  list: (filters: Record<string, unknown>) =>
    [...mediaKeys.lists(), filters] as const,
  details: () => [...mediaKeys.all, "detail"] as const,
  detail: (id: string) => [...mediaKeys.details(), id] as const,
  insights: (id: string) => [...mediaKeys.detail(id), "insights"] as const,
  status: (id: string) => [...mediaKeys.detail(id), "status"] as const,
  textInsight: (mediaId: string) => [...mediaKeys.all, "text-insight", mediaId] as const,
  transcript: (id: string) => [...mediaKeys.detail(id), "transcript"] as const,
  transcriptRevisions: (id: string) => [...mediaKeys.detail(id), "revisions"] as const,
  transcriptRevision: (revisionId: string) => [...mediaKeys.all, "transcript-revision", revisionId] as const,
  transcriptSource: (id: string) => [...mediaKeys.transcript(id), "source"] as const,
  caption: (id: string) => [...mediaKeys.detail(id), "caption"] as const,
  estimate: (items: unknown) => [...mediaKeys.all, "estimate", items] as const,
};

export const folderKeys = {
  all: ["folder"] as const,
  lists: () => [...folderKeys.all, "list"] as const,
  list: (filters: Record<string, unknown>) =>
    [...folderKeys.lists(), filters] as const,
  details: () => [...folderKeys.all, "detail"] as const,
  detail: (id: string) => [...folderKeys.details(), id] as const,
  insights: (id: string) => [...folderKeys.detail(id), "insights"] as const,
  views: (id: string) => [...folderKeys.detail(id), "views"] as const,
};

export const userKeys = {
  all: ["user"] as const,
  me: () => [...userKeys.all, "me"] as const,
  profile: () => [...userKeys.all, "profile"] as const,
  usage: () => [...userKeys.all, "usage"] as const,
  mediaStats: (type: "audio" | "video" | "text", startDate?: string, endDate?: string) =>
    [...userKeys.usage(), "media", type, startDate, endDate] as const,
  promptStats: (startDate?: string, endDate?: string) =>
    [...userKeys.usage(), "prompts", startDate, endDate] as const,
  preferences: () => [...userKeys.all, "preferences"] as const,
  providerKeys: () => [...userKeys.all, "provider-keys"] as const,
  customization: () => [...userKeys.all, "customization"] as const,
  vocabulary: () => [...userKeys.all, "vocabulary"] as const,
  linkedAccounts: () => [...userKeys.all, "linked-accounts"] as const,
  languages: () => [...userKeys.all, "languages"] as const,
  team: () => [...userKeys.all, "team"] as const,
  teamMembers: (filters?: Record<string, unknown>) =>
    [...userKeys.team(), "members", ...(filters ? [filters] : [])] as const,
  teamInvites: () => [...userKeys.team(), "invites"] as const,
  teamGroups: () => [...userKeys.team(), "groups"] as const,
  assignToList: () => [...userKeys.team(), "assign-to-list"] as const,
};

export const domainKeys = {
  all: ["domain"] as const,
  lists: () => [...domainKeys.all, "list"] as const,
  list: (serviceType?: string) =>
    [...domainKeys.lists(), serviceType] as const,
  detail: (id: string) => [...domainKeys.all, "detail", id] as const,
};

export const chatKeys = {
  all: ["chat"] as const,
  lists: () => [...chatKeys.all, "list"] as const,
  list: (filters: Record<string, unknown>) =>
    [...chatKeys.lists(), filters] as const,
  details: () => [...chatKeys.all, "detail"] as const,
  detail: (id: string) => [...chatKeys.details(), id] as const,
  // No-arg form returns the bare prefix so invalidateQueries matches every history query regardless of params.
  history: (params?: Record<string, unknown> | number) =>
    params === undefined
      ? ([...chatKeys.all, "history"] as const)
      : ([...chatKeys.all, "history", params] as const),
  messages: (params: Record<string, unknown>) =>
    [...chatKeys.all, "messages", params] as const,
  templates: (assistantTemplateId?: string) =>
    [...chatKeys.all, "templates", assistantTemplateId] as const,
  suggestions: (type?: string, query?: string) => [...chatKeys.all, "suggestions", type, query] as const,
  threads: () => [...chatKeys.all, "threads"] as const,
  total: () => [...chatKeys.all, "total"] as const,
  models: () => [...chatKeys.all, "models"] as const,
  variables: () => [...chatKeys.all, "variables"] as const,
  analysisQuote: (mediaId: string, analysisInput: string, modelId?: string) =>
    [...chatKeys.all, "analysis-quote", mediaId, analysisInput, modelId] as const,
};

export const subscriptionKeys = {
  all: ["subscription"] as const,
  current: () => [...subscriptionKeys.all, "current"] as const,
  plans: () => [...subscriptionKeys.all, "plans"] as const,
  invoices: (paymentSource?: string) =>
    [...subscriptionKeys.all, "invoices", paymentSource] as const,
  upcomingInvoice: (paymentSource?: string) =>
    [...subscriptionKeys.all, "upcoming-invoice", paymentSource] as const,
  transactions: (paymentSource?: string) =>
    [...subscriptionKeys.all, "transactions", paymentSource] as const,
  fileSizeUsage: () => [...subscriptionKeys.all, "file-size-usage"] as const,
  intent: () => [...subscriptionKeys.all, "intent"] as const,
  paddleTransaction: () =>
    [...subscriptionKeys.all, "paddle-transaction"] as const,
};

export const paymentKeys = {
  all: ["payment"] as const,
  balance: () => [...paymentKeys.all, "balance"] as const,
  sources: (paymentSource?: string) =>
    [...paymentKeys.all, "sources", paymentSource] as const,
  autoReload: () => [...paymentKeys.all, "auto-reload"] as const,
  taxInfo: () => [...paymentKeys.all, "tax-info"] as const,
  billingAddresses: () => [...paymentKeys.all, "billing-addresses"] as const,
};

export const offerKeys = {
  all: ["offer"] as const,
  lists: () => [...offerKeys.all, "list"] as const,
  detail: (id: string) => [...offerKeys.all, "detail", id] as const,
  special: () => [...offerKeys.all, "special"] as const,
  byToken: (token: string) => [...offerKeys.all, "by-token", token] as const,
};

export const crmKeys = {
  all: ["crm"] as const,
  fields: (provider: string, object: string) => [...crmKeys.all, "fields", provider, object] as const,
  records: (provider: string, object: string, q: string) => [...crmKeys.all, "records", provider, object, q] as const,
  settings: (provider: string) => [...crmKeys.all, "settings", provider] as const,
  media: (mediaId: string) => [...crmKeys.all, "media", mediaId] as const,
};

export const integrationKeys = {
  all: ["integration"] as const,
  lists: () => [...integrationKeys.all, "list"] as const,
  accounts: () => [...integrationKeys.all, "accounts"] as const,
  detail: (id: string) => [...integrationKeys.all, "detail", id] as const,
  calendar: (type: string) =>
    [...integrationKeys.all, "calendar", type] as const,
  calendarEvents: (type: string, filters: Record<string, unknown>) =>
    [...integrationKeys.calendar(type), "events", filters] as const,
  slack: () => [...integrationKeys.all, "slack"] as const,
  slackChannels: (accountId: string) =>
    [...integrationKeys.slack(), "channels", accountId] as const,
  vimeo: () => [...integrationKeys.all, "vimeo"] as const,
  vimeoVideos: (filters: Record<string, unknown>) =>
    [...integrationKeys.vimeo(), "videos", filters] as const,
  vimeoFolders: (filters: Record<string, unknown>) =>
    [...integrationKeys.vimeo(), "folders", filters] as const,
  vimeoAlbums: (filters: Record<string, unknown>) =>
    [...integrationKeys.vimeo(), "albums", filters] as const,
  vimeoFolderVideos: (folderId: string, filters: Record<string, unknown>) =>
    [...integrationKeys.vimeo(), "folder", folderId, filters] as const,
  vimeoAlbumVideos: (albumId: string, filters: Record<string, unknown>) =>
    [...integrationKeys.vimeo(), "album", albumId, filters] as const,
  googleDrive: () => [...integrationKeys.all, "googledrive"] as const,
  catalog: () => [...integrationKeys.all, "catalog"] as const,
  calendarAccounts: () => [...integrationKeys.all, "calendar-accounts"] as const,
  calendarDisabled: () => [...integrationKeys.all, "calendar-disabled"] as const,
  uniqueCalendarEvents: () =>
    [...integrationKeys.all, "unique-calendar-events"] as const,
  lark: () => [...integrationKeys.all, "lark"] as const,
  larkStatus: () => [...integrationKeys.lark(), "status"] as const,
  larkSyncs: () => [...integrationKeys.lark(), "syncs"] as const,
  larkColumns: (syncId: string) =>
    [...integrationKeys.lark(), "columns", syncId] as const,
  larkRows: (syncId: string, filters: Record<string, unknown>) =>
    [...integrationKeys.lark(), "rows", syncId, filters] as const,
  genesys: () => [...integrationKeys.all, "genesys"] as const,
  genesysStatus: () => [...integrationKeys.genesys(), "status"] as const,
  genesysConversations: (filters: Record<string, unknown>) =>
    [...integrationKeys.genesys(), "conversations", filters] as const,
  genesysConversationRaw: (conversationId: string) =>
    [...integrationKeys.genesys(), "conversation-raw", conversationId] as const,
};

export const analyticsKeys = {
  all: ["analytics"] as const,
  usage: () => [...analyticsKeys.all, "usage"] as const,
};

export const automationKeys = {
  all: ["automation"] as const,
  lists: () => [...automationKeys.all, "list"] as const,
  list: (filters: Record<string, unknown>) =>
    [...automationKeys.lists(), filters] as const,
  detail: (id: string) => [...automationKeys.all, "detail", id] as const,
  allList: () => [...automationKeys.all, "all-list"] as const,
  catalogApps: () => [...automationKeys.all, "catalog", "apps"] as const,
  catalogTriggers: (app: string) =>
    [...automationKeys.all, "catalog", "triggers", app] as const,
  catalogActions: (app: string) =>
    [...automationKeys.all, "catalog", "actions", app] as const,
  catalogTemplatesAll: () => [...automationKeys.all, "catalog", "templates"] as const,
  catalogTemplates: (provider: string) =>
    [...automationKeys.catalogTemplatesAll(), provider] as const,
  runs: (id: string) => [...automationKeys.all, "runs", id] as const,
  runHistory: (id: string, filters: Record<string, unknown>) =>
    [...automationKeys.runs(id), "history", filters] as const,
  runDetail: (id: string, runId: string) =>
    [...automationKeys.runs(id), "detail", runId] as const,
  runStats: (id: string, days: number) =>
    [...automationKeys.runs(id), "stats", days] as const,
  inboundWebhook: (webhookId: string) =>
    [...automationKeys.all, "inbound-webhook", webhookId] as const,
  webhookAttempts: (webhookId: string) =>
    [...automationKeys.all, "webhook-attempts", webhookId] as const,
};

export const recorderKeys = {
  all: ["recorder"] as const,
  lists: () => [...recorderKeys.all, "list"] as const,
  list: (filters: Record<string, unknown>) =>
    [...recorderKeys.lists(), filters] as const,
  details: () => [...recorderKeys.all, "detail"] as const,
  detail: (id: string) => [...recorderKeys.details(), id] as const,
  recordings: (id: string, filters: Record<string, unknown>) =>
    [...recorderKeys.detail(id), "recordings", filters] as const,
  mediaRecording: (mediaId: string) =>
    [...recorderKeys.all, "media-recording", mediaId] as const,
  statistics: (id: string) =>
    [...recorderKeys.detail(id), "statistics"] as const,
  submissions: (startDate?: string, endDate?: string) =>
    [...recorderKeys.all, "submissions", startDate, endDate] as const,
  liveTranscriptionToken: () =>
    [...recorderKeys.all, "live-transcription-token"] as const,
};

export const meetingAssistantKeys = {
  all: ["meeting-assistant"] as const,
  details: () => [...meetingAssistantKeys.all, "details"] as const,
  events: () => [...meetingAssistantKeys.all, "events"] as const,
  eventList: (filters: Record<string, unknown>) =>
    [...meetingAssistantKeys.events(), filters] as const,
  excluded: () => [...meetingAssistantKeys.all, "excluded"] as const,
  excludedList: (filters: Record<string, unknown>) =>
    [...meetingAssistantKeys.excluded(), filters] as const,
  excludePreview: (rule: Record<string, unknown>) =>
    [...meetingAssistantKeys.all, "exclude-preview", rule] as const,
};

export const developerKeys = {
  all: ["developer"] as const,
  apiKeys: () => [...developerKeys.all, "api-keys"] as const,
  webhooks: () => [...developerKeys.all, "webhooks"] as const,
  webhookList: (filters: Record<string, unknown>) =>
    [...developerKeys.webhooks(), "list", filters] as const,
  webhookDetail: (id: string) =>
    [...developerKeys.webhooks(), "detail", id] as const,
};

export const clipKeys = {
  all: ["clip"] as const,
  lists: () => [...clipKeys.all, "list"] as const,
  list: (filters: Record<string, unknown>) =>
    [...clipKeys.lists(), filters] as const,
  detail: (id: string) => [...clipKeys.all, "detail", id] as const,
};

export const filterKeys = {
  all: ["filter"] as const,
  lists: () => [...filterKeys.all, "list"] as const,
  detail: (id: string) => [...filterKeys.all, "detail", id] as const,
};

export const notificationKeys = {
  all: ["notification"] as const,
  lists: () => [...notificationKeys.all, "list"] as const,
  list: (filters: Record<string, unknown>) =>
    [...notificationKeys.lists(), filters] as const,
  unreadCount: () => [...notificationKeys.all, "unread-count"] as const,
};

export const embedKeys = {
  all: ["embed"] as const,
  lists: () => [...embedKeys.all, "list"] as const,
  list: (filters: Record<string, unknown>) =>
    [...embedKeys.lists(), filters] as const,
  detail: (id: string) => [...embedKeys.all, "detail", id] as const,
  viewers: (id: string) => [...embedKeys.all, "viewers", id] as const,
  sessionLists: (id: string) => [...embedKeys.all, "sessions", id] as const,
  sessions: (id: string, query: IEmbedSessionListQuery) => [...embedKeys.sessionLists(id), query] as const,
  sessionSummaries: (id: string) => [...embedKeys.all, "session-summary", id] as const,
  sessionSummary: (id: string, query: IEmbedSessionSummaryQuery) => [...embedKeys.sessionSummaries(id), query] as const,
  sessionLive: (id: string, query: IEmbedSessionLiveQuery) => [...embedKeys.all, "session-live", id, query] as const,
  sessionCountries: (id: string, query: IEmbedSessionCountriesQuery) =>
    [...embedKeys.all, "session-countries", id, query] as const,
  sessionDetail: (id: string, sessionDocId: string) => [...embedKeys.all, "session-detail", id, sessionDocId] as const,
};

export const fieldDefinitionKeys = {
  all: ["field-definitions"] as const,
  list: (params: Record<string, unknown>) =>
    [...fieldDefinitionKeys.all, "list", params] as const,
};

export const exploreKeys = {
  all: ["explore"] as const,
  search: (params: Record<string, unknown>) =>
    [...exploreKeys.all, "search", params] as const,
  data: (params: Record<string, unknown>) =>
    [...exploreKeys.all, "data", params] as const,
  fields: (params: Record<string, unknown>) =>
    [...exploreKeys.all, "fields", params] as const,
  fieldMetric: (params: Record<string, unknown>) =>
    [...exploreKeys.all, "field-metric", params] as const,
  sentiment: (params: Record<string, unknown>) =>
    [...exploreKeys.all, "sentiment", params] as const,
  fieldNames: (params: Record<string, unknown>) =>
    [...exploreKeys.all, "field-names", params] as const,
  filterValues: (fieldName: string, query?: string) =>
    [...exploreKeys.all, "filter-values", fieldName, query] as const,
  statistics: (params: Record<string, unknown>) =>
    [...exploreKeys.all, "statistics", params] as const,
};

export const humanTranscriptionKeys = {
  all: ["human-transcription"] as const,
  detail: (mediaId: string) =>
    [...humanTranscriptionKeys.all, "detail", mediaId] as const,
  revision: (revisionId: string) =>
    [...humanTranscriptionKeys.all, "revision", revisionId] as const,
};

export const translateKeys = {
  all: ["translate"] as const,
  history: (mediaIds: string[]) =>
    [...translateKeys.all, "history", mediaIds] as const,
  meta: (mediaIds: string[]) =>
    [...translateKeys.all, "meta", mediaIds] as const,
};

export const dashboardKeys = {
  all: ["dashboard"] as const,
  activities: () => [...dashboardKeys.all, "activities"] as const,
  ongoingActivities: () =>
    [...dashboardKeys.all, "ongoing-activities"] as const,
  upcomingMeetings: () =>
    [...dashboardKeys.all, "upcoming-meetings"] as const,
  usage: () => [...dashboardKeys.all, "usage"] as const,
  chatUsage: (startDate?: string, endDate?: string) =>
    [...dashboardKeys.all, "chat-usage", startDate, endDate] as const,
  textNotes: (startDate?: string, endDate?: string) =>
    [...dashboardKeys.all, "text-notes", startDate, endDate] as const,
  storageStats: (
    startDate?: string,
    endDate?: string,
    folderId?: string,
    filterList?: unknown,
  ) =>
    [...dashboardKeys.all, "storage", startDate, endDate, folderId, filterList] as const,
};

// Distinct from dashboardKeys above, which keys the home activity dashboard.
export const dashboardBuilderKeys = {
  all: ["dashboards"] as const,
  lists: () => [...dashboardBuilderKeys.all, "list"] as const,
  list: () => [...dashboardBuilderKeys.lists()] as const,
  details: () => [...dashboardBuilderKeys.all, "detail"] as const,
  detail: (id: string) => [...dashboardBuilderKeys.details(), id] as const,
  // versions/chat nest under detail(id) so deleting a dashboard evicts the whole subtree at once.
  versions: (id: string) =>
    [...dashboardBuilderKeys.detail(id), "versions"] as const,
  chat: (id: string) => [...dashboardBuilderKeys.detail(id), "chat"] as const,
  // widget-data nests under detail(id) too; the hash captures everything that changes the compute.
  widgetData: (id: string, widgetId: string, hash: string) =>
    [...dashboardBuilderKeys.detail(id), "widget-data", widgetId, hash] as const,
  speakers: (scope: Record<string, unknown>) =>
    [...dashboardBuilderKeys.all, "insights", "speakers", scope] as const,
};

export const voiceAgentKeys = {
  all: ["voice-agents"] as const,
  lists: () => [...voiceAgentKeys.all, "list"] as const,
  detail: (agentId: string) => [...voiceAgentKeys.all, "detail", agentId] as const,
};

// Distinct from analyticsKeys above, which keys account usage analytics.
export const voiceAnalyticsKeys = {
  all: ["voice-analytics"] as const,
  overview: (agentId?: string) => [...voiceAnalyticsKeys.all, "overview", agentId ?? "workspace"] as const,
  agent: (params: { agentId: string; start?: string; end?: string; tz: string; compare: boolean }) =>
    [
      ...voiceAnalyticsKeys.all,
      "agent",
      params.agentId,
      params.start ?? "all",
      params.end ?? "now",
      params.tz,
      params.compare,
    ] as const,
  funnelSummaries: () => [...voiceAnalyticsKeys.all, "funnel-summaries"] as const,
};

export const voiceConversationKeys = {
  all: ["voice-conversations"] as const,
  lists: () => [...voiceConversationKeys.all, "list"] as const,
  list: (params: {
    agentId?: string | string[];
    callType?: string;
    startDate?: string;
    endDate?: string;
    search?: string;
    page?: number;
    limit?: number;
  }) =>
    [
      ...voiceConversationKeys.lists(),
      (Array.isArray(params.agentId) ? params.agentId.join(",") : params.agentId) ?? null,
      params.callType ?? null,
      params.startDate ?? null,
      params.endDate ?? null,
      params.search ?? null,
      params.page ?? 1,
      params.limit ?? null,
    ] as const,
  detail: (conversationId: string) => [...voiceConversationKeys.all, "detail", conversationId] as const,
  events: (conversationId: string) => [...voiceConversationKeys.all, "events", conversationId] as const,
};

export const questionKeys = {
  all: ["voice-questions"] as const,
  fields: (agentId: string) => [...questionKeys.all, "fields", agentId] as const,
  templates: () => [...questionKeys.all, "templates"] as const,
};

export const phoneNumberKeys = {
  all: ["voice-phone-numbers"] as const,
  lists: () => [...phoneNumberKeys.all, "list"] as const,
  detail: (phoneNumberId: string) => [...phoneNumberKeys.all, "detail", phoneNumberId] as const,
};

export const testingKeys = {
  all: ["voice-testing"] as const,
  suite: (agentId: string) => [...testingKeys.all, "suite", agentId] as const,
  activeRun: (agentId: string) => [...testingKeys.all, "active-run", agentId] as const,
  runs: (agentId: string) => [...testingKeys.all, "runs", agentId] as const,
  run: (agentId: string, runId: string) => [...testingKeys.all, "run", agentId, runId] as const,
  baseline: (agentId: string) => [...testingKeys.all, "baseline", agentId] as const,
  scoreHistory: (agentId: string) => [...testingKeys.all, "score-history", agentId] as const,
};

export const pronunciationKeys = {
  all: ["voice-pronunciation"] as const,
  rules: (agentId: string) => [...pronunciationKeys.all, "rules", agentId] as const,
};

export const kbGapKeys = {
  all: ["voice-kb-gaps"] as const,
  list: (agentId: string) => [...kbGapKeys.all, "list", agentId] as const,
};

export const faqKeys = {
  all: ["voice-faqs"] as const,
  list: (agentId: string) => [...faqKeys.all, "list", agentId] as const,
};

export const sttLexiconKeys = {
  all: ["voice-stt-lexicon"] as const,
  suggestions: (agentId: string) => [...sttLexiconKeys.all, "suggestions", agentId] as const,
};

export const agentResourceKeys = {
  all: ["voice-agent-resources"] as const,
  lists: (agentId: string) => [...agentResourceKeys.all, agentId, "list"] as const,
  list: (agentId: string, params: { search?: string; page?: number; limit?: number }) =>
    [...agentResourceKeys.lists(agentId), params] as const,
};

export const avatarKeys = {
  all: ["voice-avatars"] as const,
  lists: () => [...avatarKeys.all, "list"] as const,
};

export const voiceKeys = {
  all: ["voice-voices"] as const,
  lists: () => [...voiceKeys.all, "list"] as const,
  detail: (id: string) => [...voiceKeys.all, "detail", id] as const,
};

export const knowledgeBaseCollectionKeys = {
  all: ["knowledge-bases"] as const,
  lists: () => [...knowledgeBaseCollectionKeys.all, "list"] as const,
  detail: (kbId: string) =>
    [...knowledgeBaseCollectionKeys.all, "detail", kbId] as const,
  documents: (kbId: string) =>
    [...knowledgeBaseCollectionKeys.all, "documents", kbId] as const,
  /** Held apart from `documents` so a document-list write does not refetch open text. */
  documentText: (kbId: string, docId: string) =>
    [...knowledgeBaseCollectionKeys.all, "document-text", kbId, docId] as const,
  owners: (kbId: string) =>
    [...knowledgeBaseCollectionKeys.all, "owners", kbId] as const,
  /** Prefix matching every collection's owner list — a write that can move any of them. */
  ownersAll: () => [...knowledgeBaseCollectionKeys.all, "owners"] as const,
  ownerCollections: (ownerType: KnowledgeBaseOwnerType, ownerId: string) =>
    [
      ...knowledgeBaseCollectionKeys.all,
      "owner",
      ownerType,
      ownerId,
    ] as const,
};

export const narrativeInsightsKeys = {
  all: ["narrative-insights"] as const,
  themeClusters: (params: {
    startDate: string;
    endDate: string;
    top?: number;
    folderId?: string;
    filterList?: unknown;
  }) => [...narrativeInsightsKeys.all, "theme-clusters", params] as const,
  teamStats: () => [...narrativeInsightsKeys.all, "team-stats"] as const,
  snapshot: (
    startDate?: string,
    endDate?: string,
    folderId?: string,
    filterList?: unknown,
  ) =>
    [...narrativeInsightsKeys.all, "snapshot", startDate, endDate, folderId, filterList] as const,
  teamActivity: (
    startDate: string,
    endDate: string,
    folderId?: string,
    filterList?: unknown,
  ) =>
    [...narrativeInsightsKeys.all, "team-activity", startDate, endDate, folderId, filterList] as const,
};

export const labelKeys = {
  all: ["labels"] as const,
  lists: () => [...labelKeys.all, "list"] as const,
  list: (filters: Record<string, unknown>) => [...labelKeys.lists(), filters] as const,
};

export const mediaLabelKeys = {
  all: ["media-labels"] as const,
  lists: () => [...mediaLabelKeys.all, "list"] as const,
  list: (mediaId: string) => [...mediaLabelKeys.lists(), mediaId] as const,
};

export const mediaCommentKeys = {
  all: ["media-comments"] as const,
  lists: () => [...mediaCommentKeys.all, "list"] as const,
  list: (mediaId: string) => [...mediaCommentKeys.lists(), mediaId] as const,
};
